import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileCheck2, UploadCloud, Loader2, Download, FileText, Sparkles, X, Send, RefreshCw, SlidersHorizontal } from "lucide-react";

const FT = { docx: "Word", xlsx: "Excel", pdf: "PDF" };

export default function AuditPrep() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null);      // { session_id, meta, filename }
  const [messages, setMessages] = useState([]);        // { role, content, job? }
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const pick = (f) => {
    if (!f) return;
    if (!/\.(docx|xlsx|pdf)$/i.test(f.name)) { toast.error("Choose a .docx, .xlsx or fillable .pdf file"); return; }
    setFile(f);
  };

  const start = async () => {
    if (!file) return;
    setStarting(true);
    try {
      const res = await api.auditPrepStart(file);
      setSession({ session_id: res.session_id, meta: res.meta, filename: file.name });
      setMessages([{ role: "assistant", content: res.greeting }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read that document"); }
    finally { setStarting(false); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await api.auditPrepMessage(session.session_id, text);
      setMessages((m) => [...m, { role: "assistant", content: res.reply, job: res.job_id ? res : null }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The assistant could not respond");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit an error. Please try again." }]);
    } finally { setSending(false); }
  };

  const download = async (job) => {
    try {
      const res = await api.auditPrepDownload(job.job_id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = job.filename || "audit_draft";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.status === 404 ? "This draft expired — ask me to generate again" : "Download failed"); }
  };

  const reset = () => { setSession(null); setMessages([]); setFile(null); setInput(""); };

  return (
    <div className="p-6 max-w-3xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 3rem)" }} data-testid="audit-prep-page">
      <div className="flex items-center gap-2">
        <FileCheck2 className="h-5 w-5 text-[#bf3b4f]" />
        <h1 className="text-2xl font-display font-bold text-slate-800">Audit Prep AI</h1>
        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Admin</span>
        <Link to="/settings/ai/audit_prep" className="ml-auto text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="audit-ai-settings">
          <SlidersHorizontal className="h-3.5 w-3.5" /> AI settings
        </Link>
        {session && (
          <button onClick={reset} className="ml-3 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="audit-new">
            <RefreshCw className="h-3.5 w-3.5" /> New form
          </button>
        )}
      </div>

      {!session ? (
        <>
          <p className="text-sm text-slate-500 mt-1">
            Upload an auditor's form (.docx, .xlsx or fillable PDF). I'll read it, talk through which staff to include and
            anything the system is missing, then fill it and hand you a draft to finish — anything unknown gets marked <span className="font-semibold">NEED INPUT</span>.
          </p>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
            className={`mt-5 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragOver ? "border-[#bf3b4f] bg-rose-50/50" : "border-slate-300 bg-white"}`}
            data-testid="audit-dropzone"
          >
            <UploadCloud className="h-8 w-8 mx-auto text-slate-400" />
            <p className="text-sm text-slate-600 mt-2">Drag a form here, or</p>
            <input ref={inputRef} type="file" accept=".docx,.xlsx,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="audit-file-input" />
            <Button variant="outline" size="sm" className="mt-2 h-8 text-xs" onClick={() => inputRef.current?.click()} data-testid="audit-browse">Choose file</Button>
            {file && (
              <div className="mt-4 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full pl-3 pr-1 py-1 text-xs" data-testid="audit-file-chip">
                <FileText className="h-3.5 w-3.5 text-slate-500" />
                <span className="font-medium text-slate-700 max-w-[280px] truncate">{file.name}</span>
                <button onClick={() => setFile(null)} className="h-5 w-5 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400" data-testid="audit-file-clear"><X className="h-3 w-3" /></button>
              </div>
            )}
          </div>
          <Button onClick={start} disabled={!file || starting} className="mt-4 h-10 text-sm" style={{ backgroundColor: "#bf3b4f" }} data-testid="audit-start-btn">
            {starting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Reading form…</> : <><Sparkles className="h-4 w-4 mr-2" /> Start</>}
          </Button>
        </>
      ) : (
        <div className="mt-4 flex-1 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b bg-slate-50 flex items-center gap-2 text-xs text-slate-600">
            <FileText className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-medium truncate">{session.filename}</span>
            <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{FT[session.meta?.ext] || session.meta?.ext}</span>
            <span className="text-slate-400">· {session.meta?.unit_label}</span>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[340px]" data-testid="audit-chat">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#bf3b4f] text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`} data-testid={`audit-msg-${m.role}`}>
                  {m.content.split(/(\*\*[^*]+\*\*)/g).map((seg, k) => seg.startsWith("**") && seg.endsWith("**")
                    ? <strong key={k}>{seg.slice(2, -2)}</strong> : <span key={k}>{seg}</span>)}
                  {m.job && (
                    <div className="mt-3 space-y-2">
                      <Button onClick={() => download({ job_id: m.job.job_id, filename: m.job.filename })} size="sm" className="h-8 text-xs block" style={{ backgroundColor: "#13b5ea" }} data-testid="audit-download-btn">
                        <Download className="h-3.5 w-3.5 mr-1.5 inline" /> Download {FT[session.meta?.ext] || "file"}
                      </Button>
                      {m.job.checklist_job_id && (
                        <Button onClick={() => download({ job_id: m.job.checklist_job_id, filename: m.job.checklist_filename })} size="sm" variant="outline" className="h-8 text-xs block border-slate-300" data-testid="audit-checklist-btn">
                          <Download className="h-3.5 w-3.5 mr-1.5 inline" /> Download gap checklist
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div></div>
            )}
          </div>

          <div className="border-t p-3 flex items-end gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="e.g. add Aisha and Alex, then generate" className="flex-1 h-10 text-sm" disabled={sending} data-testid="audit-input" />
            <Button onClick={send} disabled={sending || !input.trim()} className="h-10 w-10 p-0" style={{ backgroundColor: "#bf3b4f" }} data-testid="audit-send"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
